import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { useCallback, useEffect } from "react";
import { UploadDto, bffApi } from "../../../autogen/bff-api";
import { useApiError } from "../../errors/useApiError";
import { getPusher } from "../../pusher";
import { useAppDispatch } from "../../redux/hooks";

export type DocumentUploadContext =
  | "SourcingEvent"
  | "SourcingEventV2"
  | "Bid"
  | "PublicBid"
  | "Contract"
  | "ContractMessageAttachment"
  | "Project"
  | "NegotiationRound"
  | "Need"
  | "PublicBidRejection"
  | "PublicBidQualificationEvidence"
  | "SeV2QuestionAttachment"
  | "SeV2AnswerAttachment"
  | "SeV2AnnouncementAttachment";

export const useDocumentsUploader = ({
  entityId,
  eventId,
  entityType,
  refetch,
  qualificationCriterionKey,
  questionId,
}: {
  entityType: DocumentUploadContext;
  entityId: string;
  eventId?: string;
  refetch: () => void;
  qualificationCriterionKey?: string;
  questionId?: string;
}) => {
  const dispatch = useAppDispatch();
  const displayer = useApiError();

  useEffect(() => {
    const channel = getPusher().subscribe(entityId);
    channel.bind("document-added", refetch);

    return () => {
      channel.unbind("document-added", refetch);
    };
  }, [entityId, refetch]);

  const createPostRequest = useCallback(
    async (file: {
      id: string;
      name: string;
      extension: string;
      file: File;
    }): Promise<
      { data: UploadDto } | { error: FetchBaseQueryError | SerializedError }
    > => {
      switch (entityType) {
        case "Bid": {
          const response = dispatch(
            bffApi.endpoints.createUploadForBseBid.initiate({
              bidId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "PublicBidQualificationEvidence": {
          if (!qualificationCriterionKey)
            throw new Error("Missing qualification criterion key");
          const response = dispatch(
            bffApi.endpoints.createUploadForPublicBidQualificationEvidence.initiate(
              {
                bidId: entityId,
                uploadId: file.id,
                createPublicBidQualificationEvidenceUploadRequestDto: {
                  fileName: file.name,
                  fileSize: file.file.size,
                  fileExtension: file.extension,
                  mimeType: file.file.type,
                  criterionKey: qualificationCriterionKey,
                },
              },
            ),
          );
          response.reset();
          return await response;
        }
        case "PublicBidRejection": {
          if (!eventId) throw new Error("Missing event id");
          const response = dispatch(
            bffApi.endpoints.createUploadForPublicBidRejection.initiate({
              eventId,
              bidId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "PublicBid": {
          const response = dispatch(
            bffApi.endpoints.createUploadForPublicBid.initiate({
              bidId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "Contract": {
          const response = dispatch(
            bffApi.endpoints.createUploadForContract.initiate({
              contractId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "ContractMessageAttachment": {
          const response = dispatch(
            bffApi.endpoints.createUploadForContractMessageAttachment.initiate({
              contractId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SeV2QuestionAttachment": {
          const response = dispatch(
            bffApi.endpoints.createUploadForSeV2QuestionAttachment.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SeV2AnswerAttachment": {
          if (!questionId) throw new Error("Missing question id");
          const response = dispatch(
            bffApi.endpoints.createUploadForSeV2AnswerAttachment.initiate({
              eventId: entityId,
              questionId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SeV2AnnouncementAttachment": {
          const response = dispatch(
            bffApi.endpoints.createUploadForSeV2AnnouncementAttachment.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SourcingEvent": {
          const response = dispatch(
            bffApi.endpoints.createUploadForBse.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SourcingEventV2": {
          const response = dispatch(
            bffApi.endpoints.createUploadForSourcingEventV2.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "NegotiationRound": {
          const response = dispatch(
            bffApi.endpoints.createUploadForNegotiationRound.initiate({
              negotiationRoundId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "Project": {
          const response = dispatch(
            bffApi.endpoints.createUploadForProject.initiate({
              projectId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "Need": {
          const response = dispatch(
            bffApi.endpoints.createUploadForNeed.initiate({
              needId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
      }
    },
    [dispatch, entityId, entityType, eventId, qualificationCriterionKey, questionId],
  );

  const upload = useCallback(
    async (file: {
      id: string;
      name: string;
      extension: string;
      file: File;
    }): Promise<string | null> => {
      const postRequestResult = await createPostRequest(file);

      if ("data" in postRequestResult) {
        const data = postRequestResult.data;
        const form = new FormData();

        form.append("x-amz-date", data.renamedXAmzDate);
        form.append("x-amz-signature", data.renamedXAmzSignature);
        form.append("x-amz-algorithm", data.renamedXAmzAlgorithm);
        form.append("x-amz-credential", data.renamedXAmzCredential);
        form.append("policy", data.policy);
        form.append("key", data.key);
        form.append("file", file.file);

        // Post to the bucket URL the BFF signed the policy for (matches the Uppy uploader). Using a
        // hardcoded VITE_S3_BASE_URL instead drifts from the BFF's actual bucket and gets a 403.
        const response = await fetch(data.url, {
          method: "POST",
          body: form,
        });

        if (!response.ok) {
          displayer.trigger({
            message: `could not perform upload, response: ${response.body}`,
          });
          return null;
        }

        return data.key;
      } else {
        displayer.trigger(postRequestResult.error);
        return null;
      }
    },
    [createPostRequest, displayer],
  );

  return {
    upload,
    createPostRequest,
  };
};
